import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';
import {
  HERO_ZONE,
  mapHeroBannerRow,
  type HeroBanner,
  type HeroBannerInput,
} from '@/lib/heroBanners';

const TABLE = 'accueil_bannieres';

interface UseHeroBannersResult {
  banners: HeroBanner[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  create: (input: HeroBannerInput) => Promise<void>;
  update: (id: number, input: HeroBannerInput) => Promise<void>;
  remove: (id: number) => Promise<void>;
  move: (id: number, direction: 'up' | 'down') => Promise<void>;
  /** Réordonne les bannières selon une liste d'ids (glisser-déposer). */
  reorder: (orderedIds: number[]) => Promise<void>;
}

/** Convertit une valeur de date locale vide/pleine en ISO (ou null). */
function toIsoOrNull(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

/**
 * Gère les bannières hero d'une boutique (table `accueil_bannieres`, zone "hero").
 * `merchantId` = idcommerce du marchand (isolation multi-tenant stricte).
 */
export function useHeroBanners(merchantId: number | null): UseHeroBannersResult {
  const [banners, setBanners] = useState<HeroBanner[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!merchantId) {
      setBanners([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const { data, error: fetchError } = await supabase
        .from(TABLE)
        .select('*')
        .eq('idcommerce', merchantId)
        .eq('zone', HERO_ZONE)
        .order('ordre', { ascending: true })
        .order('id', { ascending: true });
      if (fetchError) throw fetchError;
      setBanners((data || []).map((row) => mapHeroBannerRow(row as Record<string, unknown>)));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Impossible de charger les bannières.');
      setBanners([]);
    } finally {
      setLoading(false);
    }
  }, [merchantId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const buildPayload = useCallback(
    (input: HeroBannerInput, order: number) => ({
      idcommerce: merchantId,
      idvendeur: merchantId ?? 0,
      zone: HERO_ZONE,
      titre: input.title.trim(),
      sous_titre: input.subtitle.trim(),
      description: input.description.trim(),
      bouton_texte: input.ctaLabel.trim(),
      bouton_lien: input.ctaUrl.trim(),
      image: input.image,
      afficher_texte: input.showText !== false,
      alignement_texte: input.textAlign || 'center',
      ordre: order,
      statut: input.active ? 1 : 0,
      date_debut: toIsoOrNull(input.startDate),
      date_fin: toIsoOrNull(input.endDate),
    }),
    [merchantId],
  );

  const create = useCallback(
    async (input: HeroBannerInput) => {
      if (!merchantId) throw new Error('Marchand introuvable.');
      const nextOrder = banners.length > 0 ? Math.max(...banners.map((b) => b.order)) + 1 : 1;
      const { error: insertError } = await supabase.from(TABLE).insert(buildPayload(input, nextOrder));
      if (insertError) throw insertError;
      await refresh();
    },
    [merchantId, banners, buildPayload, refresh],
  );

  const update = useCallback(
    async (id: number, input: HeroBannerInput) => {
      const current = banners.find((b) => b.id === id);
      const { error: updateError } = await supabase
        .from(TABLE)
        .update(buildPayload(input, current ? current.order : 1))
        .eq('id', id);
      if (updateError) throw updateError;
      await refresh();
    },
    [banners, buildPayload, refresh],
  );

  const remove = useCallback(
    async (id: number) => {
      const { error: deleteError } = await supabase.from(TABLE).delete().eq('id', id);
      if (deleteError) throw deleteError;
      await refresh();
    },
    [refresh],
  );

  /** Persiste un nouvel ordre (1..n) pour toute la liste. */
  const persistOrder = useCallback(
    async (ordered: HeroBanner[]) => {
      setBanners(ordered.map((b, i) => ({ ...b, order: i + 1 })));
      try {
        await Promise.all(
          ordered.map((b, i) =>
            supabase.from(TABLE).update({ ordre: i + 1 }).eq('id', b.id),
          ),
        );
      } finally {
        await refresh();
      }
    },
    [refresh],
  );

  const move = useCallback(
    async (id: number, direction: 'up' | 'down') => {
      const index = banners.findIndex((b) => b.id === id);
      if (index < 0) return;
      const target = direction === 'up' ? index - 1 : index + 1;
      if (target < 0 || target >= banners.length) return;
      const reordered = [...banners];
      [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
      await persistOrder(reordered);
    },
    [banners, persistOrder],
  );

  const reorder = useCallback(
    async (orderedIds: number[]) => {
      const byId = new Map(banners.map((b) => [b.id, b]));
      const next = orderedIds
        .map((id) => byId.get(id))
        .filter((b): b is HeroBanner => Boolean(b));
      if (next.length !== banners.length) return;
      await persistOrder(next);
    },
    [banners, persistOrder],
  );

  return { banners, loading, error, refresh, create, update, remove, move, reorder };
}